/**
 * `SoitaInfoStep` — the wizard's static call-info step body (027 US6,
 * FR-031; D-120 proto-convergence). Channel presentation ONLY: no data flow,
 * nothing routed, no send.
 *
 * Service-aware (proto step 19): the chosen service line decides the card.
 *
 *   - Lakikaveri (`viesti_lakikaverille`): 0200 55011; the full bullet run —
 *     hours (ma-to klo 9-14), urgent, the jäsennumero-authentication bullet
 *     with the member's own number, callback, document-prep, busy, pricing.
 *   - jäsenpalvelu (`viesti_jasenpalveluun`): 02 760 7630; a slimmer run —
 *     hours (ma-pe klo 9-11), callback, busy, pricing. NO urgent, NO
 *     jäsennumero, NO document-prep bullet.
 *
 * The member number (Lakikaveri only) is a DISPLAY READ of the already-mounted
 * member-profile context (the same read the shell's menu user-chip makes) — no
 * new port, no data flow; FR-031's no-routing stance stands. The read degrades
 * honestly: when the profile port is not wired (or still loading), the bullet
 * is simply absent rather than showing a placeholder number (P7).
 */

import { type JSX, Suspense, useContext, useEffect, useRef, useState } from 'react';

import { useTranslate } from '../../../i18n/useTranslate.js';
import { useMemberProfile } from '../../../member-profile/useMemberProfile.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { PortsContext } from '../../../providers/PortsContext.js';
import { CheckIcon, CopyIcon } from '../../../shell/icons.js';
import type { ServiceLine } from '../MessageComposePort.js';

// The NUMBER alone is centred under the heading: it sits in a shrink-wrapped
// `relative` box that `justify-center` centres, so the number's midpoint is the
// row's — and the centred heading's — midpoint. The copy button is positioned
// ABSOLUTELY to the number's right (`left-full` + an 8px gap, vertically
// centred), so it rides alongside without widening the box or dragging the
// number off-centre. The proto centres `[number][button]` AS A GROUP, leaving
// the number ~20px left of the heading's centre; diverging here is
// owner-decided (027 follow-up). The sr-only status span is itself
// position:absolute, so it never affects the layout.
const callColumnClasses = 'm-auto flex w-full max-w-2xl flex-col gap-5 px-4 pt-20 pb-6 text-center';
const numberRowClasses = 'flex items-center justify-center';
const numberWrapClasses = 'relative';
const numberClasses = 'm-0 text-3xl font-bold text-primary';
const copyButtonClasses =
  'absolute left-full top-1/2 ml-2 inline-flex h-6 w-6 -translate-y-1/2 items-center ' +
  'justify-center rounded-md text-primary ' +
  'transition-colors hover:bg-primary/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const notesTitleClasses = 'm-0 text-left text-lg font-semibold text-foreground';
const bulletListClasses = 'flex flex-col gap-3 text-left';
const bulletRowClasses = 'flex items-start gap-3';
const bulletGlyphClasses = 'leading-normal text-wizard-call-body';
const bulletTextClasses = 'm-0 flex-1 text-base leading-normal text-wizard-call-body';
const memberNumberClasses = 'font-bold text-primary';

/**
 * The jäsennumero bullet's number, read inside the Suspense boundary (the
 * profile hook resolves via React.use()).
 */
function MemberNumberInline(): JSX.Element {
  const profile = useMemberProfile();
  return <span className={memberNumberClasses}>{profile.contact.memberNumber}</span>;
}

/**
 * Renders the jäsennumero bullet only when the member-profile port is wired
 * — a graceful display read, not a hard dependency of the static step.
 */
function MemberNumberBullet(): JSX.Element | null {
  const t = useTranslate();
  const ports = useContext(PortsContext);
  if (ports?.memberProfile === undefined) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <div className={bulletRowClasses}>
        <span aria-hidden="true" className={bulletGlyphClasses}>
          •
        </span>
        <p className={bulletTextClasses}>
          {t('compose.wizard.soitaInfo.bullet.memberNumberLead')} <MemberNumberInline />
        </p>
      </div>
    </Suspense>
  );
}

export interface SoitaInfoStepProps {
  /**
   * The service line chosen on the selector. `null` degrades to the Lakikaveri
   * card (the full variant) — the step is only reached with a line set in the
   * call flow, so this is a defensive default, not a routine path.
   */
  readonly serviceLine: ServiceLine | null;
}

export function SoitaInfoStep({ serviceLine }: SoitaInfoStepProps): JSX.Element {
  const t = useTranslate();
  const isJasenpalvelu = serviceLine === 'viesti_jasenpalveluun';
  const serviceNumber = isJasenpalvelu
    ? t('compose.wizard.soitaInfo.numberJasenpalvelu')
    : t('compose.wizard.soitaInfo.number');

  // Copied feedback per the RD-006 icon-swap convention (profile copy rows):
  // success swaps the icon to a checkmark for ~2s and announces via the
  // polite status region below; a rejection leaves the button as-is.
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const hoursKey = isJasenpalvelu
    ? 'compose.wizard.soitaInfo.bullet.hoursJasenpalvelu'
    : 'compose.wizard.soitaInfo.bullet.hours';
  // Both services lead with hours; the remaining service-specific order stays.
  const bulletKeys = isJasenpalvelu
    ? ([
        'compose.wizard.soitaInfo.bullet.callback',
        'compose.wizard.soitaInfo.bullet.busy',
        'compose.wizard.soitaInfo.bullet.pricing',
      ] as const)
    : ([
        'compose.wizard.soitaInfo.bullet.callback',
        'compose.wizard.soitaInfo.bullet.prepare',
        'compose.wizard.soitaInfo.bullet.busy',
        'compose.wizard.soitaInfo.bullet.pricing',
      ] as const);

  return (
    <div className={callColumnClasses}>
      <Dialog.Title size="call">{t('compose.wizard.soitaInfo.title')}</Dialog.Title>
      <div className={numberRowClasses}>
        {/* The number defines the centred box; the copy button is positioned
            relative to it (absolute), so it sits to the right without shifting
            the number off the heading's centre. */}
        <div className={numberWrapClasses}>
          <p className={numberClasses}>{serviceNumber}</p>
          <button
            type="button"
            aria-label={
              copied
                ? t('compose.wizard.soitaInfo.copied')
                : t('compose.wizard.soitaInfo.copyNumber')
            }
            title={
              copied
                ? t('compose.wizard.soitaInfo.copied')
                : t('compose.wizard.soitaInfo.copyNumber')
            }
            className={copyButtonClasses}
            onClick={() => {
              // Best-effort affordance: absent clipboard (insecure context,
              // older engine) no-ops — the explicit guard reads clearer than
              // relying on `?.` short-circuiting the whole chain (which it
              // does; no call ever throws here) — and a write rejection
              // (permissions) is swallowed rather than surfaced.
              if (navigator.clipboard === undefined) {
                return;
              }
              navigator.clipboard.writeText(serviceNumber).then(
                () => {
                  setCopied(true);
                  if (copyTimerRef.current !== null) {
                    clearTimeout(copyTimerRef.current);
                  }
                  copyTimerRef.current = setTimeout(() => {
                    setCopied(false);
                    copyTimerRef.current = null;
                  }, 2000);
                },
                () => undefined,
              );
            }}
          >
            {copied ? (
              <CheckIcon aria-hidden="true" className="h-4 w-4 text-green-600" />
            ) : (
              <CopyIcon aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
        </div>
        {/* Persistent polite region: content change fires the announcement.
            Reverts to a space, not '' — some screen readers announce a
            cleared live region as "blank". */}
        <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {copied ? t('compose.wizard.soitaInfo.copied') : ' '}
        </span>
      </div>
      <h3 className={notesTitleClasses}>{t('compose.wizard.soitaInfo.notesTitle')}</h3>
      <div className={bulletListClasses}>
        <div className={bulletRowClasses}>
          <span aria-hidden="true" className={bulletGlyphClasses}>
            •
          </span>
          <p className={bulletTextClasses}>{t(hoursKey)}</p>
        </div>
        {/* Lakikaveri-only bullets: urgent + the jäsennumero auth bullet
            (the latter a graceful member-profile display read). */}
        {!isJasenpalvelu && (
          <>
            <div className={bulletRowClasses}>
              <span aria-hidden="true" className={bulletGlyphClasses}>
                •
              </span>
              <p className={bulletTextClasses}>{t('compose.wizard.soitaInfo.bullet.urgent')}</p>
            </div>
            <MemberNumberBullet />
          </>
        )}
        {bulletKeys.map((key) => (
          <div key={key} className={bulletRowClasses}>
            <span aria-hidden="true" className={bulletGlyphClasses}>
              •
            </span>
            <p className={bulletTextClasses}>{t(key)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
